import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getOrderStats, getPromotions } from '../services/api';
import api from '../services/api';
import './AdminDashboard.css';

const STATUS_BADGES = {
  awaiting_payment: 'badge-warning',
  processing: 'badge-info',
  shipped: 'badge-info',
  delivered: 'badge-success',
  cancelled: 'badge-danger',
};
const SYNC_SOURCE_LABELS = {
  inventory_api: 'Inventory API',
  trading_api: 'Seller Listings',
};
const TOKEN_MODE_LABELS = {
  refresh_token: 'Refresh token',
  user_token: 'Access token',
  missing: 'Missing',
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const loadEbayStatus = () => {
    api.get('/ebay/status').then(r => setEbayStatus(r.data)).catch(() => {});
  };

  const handleEbaySync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post('/ebay/sync');
      setSyncResult({ success: true, ...data });
      loadEbayStatus();
    } catch (err) {
      setSyncResult({ success: false, error: err.response?.data?.error || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadEbayStatus();
    getPromotions().then((r) => setPromotions(r.data)).catch(() => {});
    getOrderStats()
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const lastSource = syncResult?.source || ebayStatus?.lastSync?.source;
  const connected = ebayStatus?.configured !== false;
  const livePromotions = promotions.filter((promotion) => {
    const startsAt = promotion.starts_at ? new Date(promotion.starts_at).getTime() : null;
    const endsAt = promotion.ends_at ? new Date(promotion.ends_at).getTime() : null;
    const now = Date.now();
    return Boolean(
      promotion.active &&
      (!startsAt || startsAt <= now) &&
      (!endsAt || endsAt >= now)
    );
  });

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back — here's what's happening in your store.</p>
      </div>

      {/* Quick Nav */}
      <div className="quick-nav">
        <Link to="/admin/products/new" className="quick-nav-card primary">
          <span className="qn-icon">➕</span>
          <span>List New Item</span>
        </Link>
        <Link to="/admin/promotions" className="quick-nav-card">
          <span className="qn-icon">🏷️</span>
          <span>Promos &amp; Sales</span>
        </Link>
        <Link to="/admin/orders" className="quick-nav-card">
          <span className="qn-icon">📦</span>
          <span>View Orders</span>
        </Link>
        <Link to="/admin/support" className="quick-nav-card">
          <span className="qn-icon">💬</span>
          <span>Support Tickets</span>
        </Link>
        <Link to="/admin/products" className="quick-nav-card">
          <span className="qn-icon">🏪</span>
          <span>Manage Listings</span>
        </Link>
      </div>

      {/* eBay Sync Panel */}
      <div className="card ebay-sync-card">
        <div className="ebay-sync-header">
          <div className="ebay-sync-copy">
            <div className="ebay-sync-title-row">
              <h2>eBay Listing Sync</h2>
              <span className={`sync-pill ${connected ? 'connected' : 'disconnected'}`}>
                {connected ? 'Connected' : 'Needs setup'}
              </span>
            </div>
            <p className="ebay-sync-description">
              Manual sync only. Pull live eBay listings into your storefront whenever you are ready.
            </p>
            <div className="ebay-sync-meta">
              <div className="sync-meta-card">
                <span className="sync-meta-label">Imported listings</span>
                <strong>{ebayStatus?.totalSynced || 0}</strong>
              </div>
              <div className="sync-meta-card">
                <span className="sync-meta-label">Connection</span>
                <strong>{TOKEN_MODE_LABELS[ebayStatus?.tokenMode] || 'Unknown'}</strong>
              </div>
              <div className="sync-meta-card">
                <span className="sync-meta-label">Environment</span>
                <strong>{ebayStatus?.environment || 'production'}</strong>
              </div>
              <div className="sync-meta-card">
                <span className="sync-meta-label">Last source</span>
                <strong>{SYNC_SOURCE_LABELS[lastSource] || 'Not synced yet'}</strong>
              </div>
            </div>
            {ebayStatus?.lastSync && (
              <p className="ebay-sync-last-run">
                Last sync: {new Date(ebayStatus.lastSync.synced_at).toLocaleString()}
              </p>
            )}
            {!connected && (
              <p className="ebay-config-note">
                Add your eBay app keys and seller refresh token to the server environment before syncing.
              </p>
            )}
          </div>
          <button
            className="btn btn-primary"
            onClick={handleEbaySync}
            disabled={syncing || !connected}
          >
            {syncing ? 'Syncing eBay listings...' : 'Sync Now'}
          </button>
        </div>
        {syncResult && (
          <div className={`alert ${syncResult.success ? 'alert-success' : 'alert-error'} ebay-sync-alert`}>
            {syncResult.success
              ? `Sync complete — ${syncResult.created} new, ${syncResult.updated} updated, ${syncResult.total} total from ${SYNC_SOURCE_LABELS[syncResult.source] || 'eBay'}`
              : `❌ ${syncResult.error}`}
          </div>
        )}
      </div>

      <div className="card promo-dashboard-card">
        <div className="promo-dashboard-head">
          <div>
            <div className="promo-dashboard-title-row">
              <h2>Promos &amp; Sales</h2>
              <span className={`sync-pill ${livePromotions.length ? 'connected' : 'disconnected'}`}>
                {livePromotions.length ? `${livePromotions.length} live` : 'No live sale'}
              </span>
            </div>
            <p className="ebay-sync-description">
              Launch storewide sales, category discounts, or one-item promos from one place.
            </p>
          </div>
          <Link to="/admin/promotions" className="btn btn-primary">Manage promos</Link>
        </div>

        {livePromotions.length ? (
          <div className="promo-dashboard-list">
            {livePromotions.slice(0, 3).map((promotion) => (
              <div key={promotion.id} className="promo-dashboard-item">
                <strong>{promotion.name}</strong>
                <span>{promotion.discount_percent}% off</span>
                <p>{promotion.headline || promotion.description || 'Live promotion is active across the store.'}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="promo-dashboard-empty">
            No active campaigns yet. Open Promos &amp; Sales to start your next launch or flash sale.
          </p>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">${stats.totalRevenue?.toFixed(2)}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalOrders}</div>
              <div className="stat-label">Total Orders</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.pending}</div>
              <div className="stat-label">Processing</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.shipped}</div>
              <div className="stat-label">Shipped</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.activeCarts || 0}</div>
              <div className="stat-label">Active Carts</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalListingViews || 0}</div>
              <div className="stat-label">Listing Views</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.savedItemSessions || 0}</div>
              <div className="stat-label">Saved Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.abandonedCartCount || 0}</div>
              <div className="stat-label">Abandoned Carts</div>
            </div>
          </div>

          <div className="card abandoned-cart-card" style={{ marginTop: '2rem' }}>
            <div className="promo-dashboard-head">
              <div>
                <div className="promo-dashboard-title-row">
                  <h2>Abandoned Cart Watchlist</h2>
                  <span className={`sync-pill ${stats.abandonedCartCount ? 'disconnected' : 'connected'}`}>
                    {stats.abandonedCartCount ? `${stats.abandonedCartCount} to review` : 'Looking healthy'}
                  </span>
                </div>
                <p className="ebay-sync-description">
                  Anonymous carts that have been sitting without checkout. This helps you spot demand before you add email recovery later.
                </p>
              </div>
            </div>

            {stats.abandonedCarts?.length ? (
              <div className="abandoned-cart-list">
                {stats.abandonedCarts.map((cart) => (
                  <div key={cart.sessionId} className="abandoned-cart-item">
                    <div className="abandoned-cart-meta">
                      <strong>Session {cart.sessionId.slice(-8)}</strong>
                      <span>{cart.lineCount} item type{cart.lineCount === 1 ? '' : 's'} · {cart.totalUnits} unit{cart.totalUnits === 1 ? '' : 's'}</span>
                      <span>Estimated value ${cart.estimatedValue.toFixed(2)}</span>
                      <span>Last seen {new Date(cart.lastUpdated).toLocaleString()}</span>
                    </div>
                    <div className="abandoned-cart-items">
                      {cart.items.map((item, index) => (
                        <span key={`${cart.sessionId}-${index}`}>{item.title} × {item.qty}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="promo-dashboard-empty">
                No abandoned carts in the current watch window.
              </p>
            )}
          </div>

          {/* Recent Orders */}
          <div className="card" style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>Recent Orders</h2>
              <Link to="/admin/orders" className="btn btn-secondary btn-sm">View All</Link>
            </div>

            {stats.recentOrders?.length === 0 ? (
              <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>No orders yet.</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentOrders.map(order => (
                    <tr key={order.id}>
                      <td><code>{order.order_number}</code></td>
                      <td>{order.customer_name}</td>
                      <td>${order.total?.toFixed(2)}</td>
                      <td>
                        <span className={`badge ${STATUS_BADGES[order.status] || 'badge-gray'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>{new Date(order.created_at).toLocaleDateString()}</td>
                      <td><Link to={`/admin/orders`} className="btn btn-secondary btn-sm">View</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
